import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useState } from 'react';
import {
  gateForStage,
  shortDigest,
  type ApprovalDecision,
  type Engagement,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../auth/auth';

const GATE_WARNING: Record<string, string> = {
  SCOPE: 'Approving authorises testing against the hosts, limits and window in this scope version.',
  PLAN: 'Approving permits active scanning — payloads that can change state on the target.',
  REPORT:
    'Approving publishes this exact report. Your decision records its hash, so it can be shown later that what was published is what you approved.',
};

/**
 * The decision itself, shown beside the scope so the approver reads what they are authorising.
 *
 * The decision carries the gate and the scope version the page loaded. If either moved while
 * this was open the server refuses it, so an approval can never land on something the
 * approver did not see.
 */
export function ApprovalCard({ engagement }: { engagement: Engagement }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [note, setNote] = useState('');

  const gate = gateForStage(engagement.stage);
  const scopeVersion = engagement.currentScopeVersion;

  // At the Report gate the decision is about a specific report, which the approver must read.
  const reports = useQuery({
    queryKey: ['reports', engagement.id],
    queryFn: () => api.listReports(engagement.id),
    enabled: gate === 'REPORT',
  });
  const report = reports.data?.items.at(-1);

  const decide = useMutation({
    mutationFn: (decision: ApprovalDecision) => {
      if (!gate || scopeVersion === undefined) {
        throw new Error('This engagement is not waiting at a gate.');
      }
      if (gate === 'REPORT' && !report) throw new Error('There is no report to decide on yet.');
      return api.decideApproval(engagement.id, {
        gate,
        decision,
        scopeVersion,
        ...(gate === 'REPORT' && report ? { reportVersion: report.version } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['engagement', engagement.id] });
      await queryClient.invalidateQueries({ queryKey: ['approvals'] });
      await queryClient.invalidateQueries({ queryKey: ['engagements'] });
      await queryClient.invalidateQueries({ queryKey: ['approvalHistory', engagement.id] });
      await queryClient.invalidateQueries({ queryKey: ['reports', engagement.id] });
    },
  });

  if (!gate || scopeVersion === undefined) return null;

  const mayDecide = user?.role === 'approver' || user?.role === 'security-admin';

  return (
    <section className="card approval" aria-labelledby="decide-title">
      <span className="gate-tag">Approval gate · {gate}</span>
      <h3 id="decide-title">Decision required</h3>
      <p className="meta">{GATE_WARNING[gate]}</p>
      {gate === 'REPORT' ? (
        report ? (
          <p className="meta">
            You are deciding on <strong>report v{report.version}</strong>{' '}
            <span className="mono">({shortDigest(report.digest)})</span>.{' '}
            <Link
              to={`/app/engagements/${engagement.id}/reports/${report.version}`}
              className="table-link"
            >
              Read it before deciding
            </Link>
            . If it is regenerated while this is open, the decision will be refused.
          </p>
        ) : (
          <p className="meta">Loading the report…</p>
        )
      ) : (
        <p className="meta">
          You are deciding on <strong>scope v{scopeVersion}</strong>, shown above. If it is revised
          while this is open, the decision will be refused rather than applied to the new version.
        </p>
      )}

      {decide.error ? (
        <p className="login-error" role="alert">
          {decide.error instanceof ApiError
            ? decide.error.message
            : 'Could not record the decision.'}
        </p>
      ) : null}

      {mayDecide ? (
        <>
          <div className="field">
            <label htmlFor="note">Note (optional)</label>
            <input
              id="note"
              className="input"
              maxLength={1000}
              placeholder="What you checked, or why you declined"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={decide.isPending}
              onClick={() => decide.mutate('APPROVED')}
            >
              {decide.isPending ? 'Recording…' : 'Approve'}
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              disabled={decide.isPending}
              onClick={() => decide.mutate('DECLINED')}
            >
              Decline
            </button>
          </div>
        </>
      ) : (
        <p className="meta">
          Your role can read this but not decide it. An approver or a security administrator must
          record the decision.
        </p>
      )}
    </section>
  );
}
